package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Preview data for a player who will appear in R32 based on picks.
 */
data class R32PlayerPreview(
    val name: String,
    val seed: Int?,
    val country: String?,
    val playerId: String?
) {
    val displayName: String get() = name
    val hasPlayer: Boolean get() = playerId != null
}

/**
 * Represents a feeder section that feeds into a single match.
 * For 128-draw: 4 R128 matches → 2 R64 matches → 1 R32 match
 * For 64-draw: 2 R64 matches → 1 R32 match
 */
data class FeederSection(
    val targetMatchId: String,
    val targetMatchNumber: Int,
    val feederMatches: List<FeederRound>,
    val totalMatches: Int,
    val pickedMatches: Int,
    /** Preview of player 1 for the R32 match (based on picks from the final R64 match in this section) */
    val predictedPlayer1: R32PlayerPreview? = null,
    /** Preview of player 2 for the R32 match (based on picks from the final R64 match in this section) */
    val predictedPlayer2: R32PlayerPreview? = null
) {
    val isComplete: Boolean get() = pickedMatches == totalMatches
    val progressPercent: Double get() = if (totalMatches > 0) (pickedMatches.toDouble() / totalMatches * 100) else 0.0
    /** True if we have predicted both R32 players */
    val hasR32Preview: Boolean get() = predictedPlayer1 != null && predictedPlayer2 != null
    /** True if we have predicted at least one R32 player */
    val hasPartialR32Preview: Boolean get() = predictedPlayer1 != null || predictedPlayer2 != null
}

data class FeederRound(
    val round: RoundName,
    val matches: List<BracketMatchEntity>
)

/**
 * Collapsed feeder section that can be expanded to show early round matches.
 *
 * Used for 64/128 player draws to avoid overwhelming the user with too many
 * matches at once. Shows a summary with completion status, expandable to
 * reveal the full match tree for that section.
 */
@Composable
fun CollapsedFeederSection(
    section: FeederSection,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    isExpanded: Boolean,
    onToggleExpand: () -> Unit,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(8.px)
            marginBottom(8.px)
            property("box-shadow", "0 1px 3px rgba(0, 0, 0, 0.1)")
            overflow("hidden")
        }
    }) {
        // Header - always visible
        CollapsedHeader(
            section = section,
            isExpanded = isExpanded,
            onToggle = onToggleExpand
        )

        // Expandable content
        if (isExpanded) {
            ExpandedContent(
                section = section,
                picks = picks,
                isViewOnly = isViewOnly,
                onSelectWinner = onSelectWinner,
                onShowMatchDetails = onShowMatchDetails
            )
        }
    }
}

@Composable
private fun CollapsedHeader(
    section: FeederSection,
    isExpanded: Boolean,
    onToggle: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            padding(12.px, 16.px)
            cursor("pointer")
            backgroundColor(if (isExpanded) Colors.Gray50 else Colors.Surface)
            property("transition", "background-color 0.2s")
            if (section.isComplete) {
                property("border-left", "3px solid ${Colors.Success}")
            } else {
                property("border-left", "3px solid ${Colors.Primary}")
            }
        }
        onClick { onToggle() }
        attr("role", "button")
        attr("aria-expanded", isExpanded.toString())
        attr("tabindex", "0")
        onKeyDown { event ->
            if (event.key == "Enter" || event.key == " ") {
                event.preventDefault()
                onToggle()
            }
        }
    }) {
        // Top row: Section header with R32 target and status
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(8.px)
            }
        }) {
            // Left: R32 match destination (prominent)
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                }
            }) {
                // Expand/collapse icon
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                        property("transition", "transform 0.2s")
                        if (isExpanded) {
                            property("transform", "rotate(90deg)")
                        }
                    }
                }) {
                    Text("\u25B6") // Right arrow
                }

                // R32 Match badge - primary visual element
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                    }
                }) {
                    // Arrow icon pointing to R32
                    Span({
                        style {
                            fontSize(14.px)
                            color(Colors.Primary)
                        }
                    }) {
                        Text("\u2192") // Right arrow
                    }
                    Span({
                        style {
                            fontSize(13.px)
                            fontWeight(700)
                            color(Colors.Primary)
                            backgroundColor(Colors.PrimaryLight)
                            padding(3.px, 8.px)
                            borderRadius(4.px)
                        }
                    }) {
                        Text("R32 M${section.targetMatchNumber}")
                    }
                }
            }

            // Right: Completion badge
            CompletionBadge(
                picked = section.pickedMatches,
                total = section.totalMatches,
                isComplete = section.isComplete
            )
        }

        // Bottom row: R32 Player preview (shows who will play in R32 based on picks)
        R32MatchPreview(section = section)
    }
}

/**
 * Shows a preview of the R32 matchup based on early round picks.
 * Helps users understand who will face each other in R32 once early rounds complete.
 */
@Composable
private fun R32MatchPreview(section: FeederSection) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(6.px, 8.px)
            backgroundColor(Colors.SurfaceVariant)
            borderRadius(6.px)
            marginTop(4.px)
        }
    }) {
        // Player 1 preview
        PlayerPreviewChip(
            player = section.predictedPlayer1,
            label = "P1"
        )

        // VS divider
        Span({
            style {
                fontSize(10.px)
                fontWeight(600)
                color(Colors.TextSecondary)
                padding(0.px, 4.px)
            }
        }) {
            Text("vs")
        }

        // Player 2 preview
        PlayerPreviewChip(
            player = section.predictedPlayer2,
            label = "P2"
        )
    }
}

/**
 * Compact player preview chip showing predicted R32 player.
 */
@Composable
private fun PlayerPreviewChip(
    player: R32PlayerPreview?,
    @Suppress("UNUSED_PARAMETER") label: String
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            padding(3.px, 8.px)
            backgroundColor(if (player != null) Colors.Surface else Colors.Gray100)
            borderRadius(4.px)
            border(
                1.px,
                if (player != null) LineStyle.Solid else LineStyle.Dashed,
                if (player != null) Colors.BorderLight else Colors.Border
            )
            flex(1)
            minWidth(80.px)
        }
    }) {
        if (player != null) {
            // Seed badge if present
            player.seed?.let { seed ->
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(9.px)
                        fontWeight(600)
                        padding(1.px, 4.px)
                        borderRadius(3.px)
                        marginRight(4.px)
                    }
                }) {
                    Text("$seed")
                }
            }
            // Player name
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(500)
                    color(Colors.TextPrimary)
                    property("white-space", "nowrap")
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                }
            }) {
                Text(player.displayName)
            }
        } else {
            // TBD placeholder
            Span({
                style {
                    fontSize(11.px)
                    fontStyle("italic")
                    color(Colors.TextSecondary)
                }
            }) {
                Text("TBD")
            }
        }
    }
}

@Composable
private fun CompletionBadge(
    picked: Int,
    total: Int,
    isComplete: Boolean
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            padding(4.px, 10.px)
            borderRadius(20.px)
            backgroundColor(if (isComplete) Colors.SuccessLight else Colors.Gray100)
        }
    }) {
        // Icon
        Span({
            style {
                fontSize(12.px)
            }
        }) {
            Text(if (isComplete) "\u2713" else "\u25CB") // Checkmark or circle
        }

        // Count
        Span({
            style {
                fontSize(12.px)
                fontWeight(if (isComplete) 600 else 400)
                color(if (isComplete) Colors.Success else Colors.TextSecondary)
            }
        }) {
            Text("$picked/$total picks")
        }
    }
}

@Composable
private fun ExpandedContent(
    section: FeederSection,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit
) {
    Div({
        style {
            padding(16.px)
            backgroundColor(Colors.Gray50)
            property("border-top", "1px solid ${Colors.BorderLight}")
        }
        classes("feeder-expanded-content")
    }) {
        // Display matches grouped by round
        section.feederMatches.forEachIndexed { roundIndex, feederRound ->
            // Round header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    marginBottom(12.px)
                    if (roundIndex > 0) {
                        marginTop(20.px)
                    }
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(Colors.Primary)
                        backgroundColor(Colors.PrimaryLight)
                        padding(4.px, 10.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text(feederRound.round.displayName)
                }

                // Progress for this round
                val roundPicked = feederRound.matches.count { match ->
                    picks.containsKey(match.player1.id)
                }
                Span({
                    style {
                        fontSize(11.px)
                        color(Colors.TextSecondary)
                        marginLeft(12.px)
                    }
                }) {
                    Text("${roundPicked}/${feederRound.matches.size}")
                }
            }

            // Matches grid
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
                    gap(12.px)
                }
            }) {
                feederRound.matches.forEach { match ->
                    BracketMatchNode(
                        match = match,
                        selectedWinnerId = picks[match.player1.id],
                        isViewOnly = isViewOnly,
                        variant = MatchNodeVariant.COMPACT,
                        width = 200,
                        height = 65,
                        onSelectWinner = { winnerId ->
                            onSelectWinner(match.player1.id, winnerId)
                        },
                        onShowDetails = { onShowMatchDetails(match) }
                    )
                }
            }
        }

        // Flow indicator to target match
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                marginTop(16.px)
                padding(8.px)
            }
        }) {
            Span({
                style {
                    fontSize(20.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("\u2193") // Down arrow
            }
            Span({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                    marginLeft(8.px)
                }
            }) {
                Text("Feeds into Match ${section.targetMatchNumber}")
            }
        }
    }
}

/**
 * Calculate feeder sections for a bracket.
 *
 * For a 128-draw:
 * - Each R32 match has 4 R128 matches and 2 R64 matches feeding into it
 *
 * For a 64-draw:
 * - Each R32 match has 2 R64 matches feeding into it
 */
object FeederSectionCalculator {

    fun calculateFeederSections(
        rounds: List<BracketRoundEntity>,
        drawSize: Int,
        picks: Map<String, String>
    ): List<FeederSection> {
        if (drawSize < 64) return emptyList()

        val sections = mutableListOf<FeederSection>()

        // Find the R32 round (the target round for feeder sections)
        val r32Round = rounds.find { it.name == RoundName.R32 } ?: return emptyList()
        val r64Round = rounds.find { it.name == RoundName.R64 }

        r32Round.matches.forEach { targetMatch ->
            val feederRounds = mutableListOf<FeederRound>()
            var totalMatches = 0
            var pickedMatches = 0
            var r64Matches: List<BracketMatchEntity> = emptyList()

            if (drawSize >= 128) {
                // Add R128 matches that feed into this R32 slot
                val r128Round = rounds.find { it.name == RoundName.R128 }
                if (r128Round != null) {
                    // Calculate which R128 matches feed into this R32 match
                    val matchIndex = r32Round.matches.indexOf(targetMatch)
                    val r128StartIndex = matchIndex * 4
                    val r128MatchesForSection = r128Round.matches.subList(
                        r128StartIndex.coerceAtMost(r128Round.matches.size),
                        (r128StartIndex + 4).coerceAtMost(r128Round.matches.size)
                    )
                    if (r128MatchesForSection.isNotEmpty()) {
                        feederRounds.add(FeederRound(RoundName.R128, r128MatchesForSection))
                        totalMatches += r128MatchesForSection.size
                        pickedMatches += r128MatchesForSection.count { picks.containsKey(it.player1.id) }
                    }
                }
            }

            if (drawSize >= 64 && r64Round != null) {
                // Add R64 matches that feed into this R32 slot
                val matchIndex = r32Round.matches.indexOf(targetMatch)
                val r64StartIndex = matchIndex * 2
                r64Matches = r64Round.matches.subList(
                    r64StartIndex.coerceAtMost(r64Round.matches.size),
                    (r64StartIndex + 2).coerceAtMost(r64Round.matches.size)
                )
                if (r64Matches.isNotEmpty()) {
                    feederRounds.add(FeederRound(RoundName.R64, r64Matches))
                    totalMatches += r64Matches.size
                    pickedMatches += r64Matches.count { picks.containsKey(it.player1.id) }
                }
            }

            // Calculate R32 player previews based on R64 picks
            val (predictedPlayer1, predictedPlayer2) = calculateR32PlayerPreviews(r64Matches, picks)

            if (feederRounds.isNotEmpty()) {
                sections.add(
                    FeederSection(
                        targetMatchId = targetMatch.id,
                        targetMatchNumber = targetMatch.matchNumber,
                        feederMatches = feederRounds,
                        totalMatches = totalMatches,
                        pickedMatches = pickedMatches,
                        predictedPlayer1 = predictedPlayer1,
                        predictedPlayer2 = predictedPlayer2
                    )
                )
            }
        }

        return sections
    }

    /**
     * Calculate which players will appear in R32 based on R64 picks.
     * For each R32 match, there are 2 R64 matches feeding into it.
     * The winner of R64 Match 1 becomes Player 1 in R32, winner of R64 Match 2 becomes Player 2.
     * If no pick has been made, shows the R64 matchup (e.g., "Sinner / Alcaraz").
     */
    private fun calculateR32PlayerPreviews(
        r64Matches: List<BracketMatchEntity>,
        picks: Map<String, String>
    ): Pair<R32PlayerPreview?, R32PlayerPreview?> {
        if (r64Matches.size < 2) return Pair(null, null)

        val player1 = getPickedWinnerAsPreview(r64Matches[0], picks)
            ?: getMatchupAsPreview(r64Matches[0])
        val player2 = getPickedWinnerAsPreview(r64Matches[1], picks)
            ?: getMatchupAsPreview(r64Matches[1])

        return Pair(player1, player2)
    }

    /**
     * Get a preview showing both players from a match (when no pick made yet).
     */
    private fun getMatchupAsPreview(match: BracketMatchEntity): R32PlayerPreview {
        val name1 = match.player1.displayName.split(" ").lastOrNull() ?: match.player1.displayName
        val name2 = match.player2.displayName.split(" ").lastOrNull() ?: match.player2.displayName
        return R32PlayerPreview(
            name = "$name1 / $name2",
            seed = null,
            country = null,
            playerId = null
        )
    }

    /**
     * Get the picked winner from a match as a player preview.
     */
    private fun getPickedWinnerAsPreview(
        match: BracketMatchEntity,
        picks: Map<String, String>
    ): R32PlayerPreview? {
        val pickedWinnerId = picks[match.player1.id] ?: return null

        val winner = when (pickedWinnerId) {
            match.player1.playerId -> match.player1
            match.player2.playerId -> match.player2
            else -> return null
        }

        return R32PlayerPreview(
            name = winner.displayName,
            seed = winner.seed,
            country = winner.playerCountry,
            playerId = winner.playerId
        )
    }
}

/**
 * Compact feeder indicator shown in the tree view.
 * Displays a small badge that can be clicked to expand the section.
 */
@Composable
fun FeederIndicator(
    section: FeederSection,
    isExpanded: Boolean,
    onToggle: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            padding(4.px, 8.px)
            backgroundColor(if (section.isComplete) Colors.SuccessLight else Colors.SurfaceVariant)
            borderRadius(4.px)
            cursor("pointer")
            fontSize(10.px)
            property("transition", "all 0.2s")
        }
        onClick { onToggle() }
        attr("title", "Click to ${if (isExpanded) "collapse" else "expand"} early rounds")
    }) {
        Span({
            style {
                property("transition", "transform 0.2s")
                if (isExpanded) {
                    property("transform", "rotate(90deg)")
                }
            }
        }) {
            Text(if (isExpanded) "\u25BC" else "\u25B6")
        }
        Text("${section.pickedMatches}/${section.totalMatches}")
    }
}
